import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Typography,
  Grid,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { MenuContainer, ReviewTableContainer, ScenarioMenuContainer } from './SegmentPackDeepDive.Styled';
import { formatNumber } from '../../../../utils/helper';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import {
  fetchSegmentDeepDive,
  setActiveMenu,
  setAnchorEl,
  setExpandedRows,
  setFilterText,
  setOrderBy,
  setSelectedScenario,
  setSortDirection,
} from '../../../../features/outputSegmentDeepDive/outputSegmentDeepDiveSlice';
import { hideLoader, showLoader } from '../../../../features/ui/loaderSlice';
import { RootState } from '../../../../state/store';
import { SCENARIO_OUTPUT_DATA } from '../../../../utils/dataConstants';
import { SelectMapperKeys, ViewByMapperKeysForDeepdive } from '../../../../types/common';
import { StyledCheckbox } from '../../../../utils/commonStyles.Styled';

const headerCellStyle = {
  borderRight: '2px solid white',
  overflow: 'hidden',
  fontSize: '10.14px',
  fontWeight: 600,
  maxHeight: '10px',
  padding: '0px',
  align: 'center',
  textAlign: 'center',
  whiteSpace: 'break-elipses',

  height: '34.47px',
  lineHeight: '12.36px',
  background: '#F4F4F4',
  paddingLeft: '10px',
  paddingRight: '10px',
  width: '10px',
  borderBottom: '1.01px solid #E81C0E',
};

const bodyCellStyle = {
  borderRight: '2px solid white',
  overflow: 'hidden',
  fontSize: '9.08px',
  fontWeight: 500,
  maxHeight: '30.88px !important',
  height: '30.88px !important',
  minHeight: '20.88px !important',
  padding: '0px',
  align: 'center',
  textAlign: 'center',
  whiteSpace: 'break-elipses',
  lineHeight: '11.07px',
  width: '80px',
  borderBottom: '2px solid white',
};

const typographyStyle = {
  fontSize: '10.89px',
  fontWeight: 500,
  textAlign: 'center',
  lineHeight: '16.34px',
};

const smallTypographyStyle = {
  fontSize: '9.08px',
  fontWeight: 700,
  textAlign: 'center',
  color: '#2EB28B',
  marginLeft: '5px',
};

const NestedTable: React.FC<any> = ({ data }) => {
  const dispatch = useAppDispatch();
  const {
    expandedRows,
    sortDirection,
    orderBy,
    filterText,
    selectedScenarios,
    anchorEl,
    activeMenu,
    scenarios,
    selectedDeepdiveToggle,
  } = useAppSelector((state: RootState) => state.outputSegmentDeepDive);

  const { selectedToggle, selectedViewBy, enabledKeys } = useAppSelector((state: RootState) => state.outputFilters);
  const { selectedBrands } = useAppSelector((state: RootState) => state.brand);

  const handleExpandClick = (segment: string) => {
    dispatch(setExpandedRows(segment));
  };

  const handleRequestSort = (property: any) => {
    const isAsc = orderBy === property && sortDirection === 'asc';
    dispatch(setSortDirection(isAsc ? 'desc' : 'asc'));
    dispatch(setOrderBy(property));
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilterText(event.target.value));
  };

  const sortedFilteredData = [...data]
    .filter((item) => item.segment.toLowerCase().includes(filterText.toLowerCase()))
    .sort((a: any, b: any) => {
      const isAsc = sortDirection === 'asc';
      if (a[orderBy] < b[orderBy]) return isAsc ? -1 : 1;
      if (a[orderBy] > b[orderBy]) return isAsc ? 1 : -1;
      return 0;
    });

  const subHeaders = (val: string) => {
    const headers =
      selectedDeepdiveToggle === 'rgm'
        ? [
            { label: 'Volume UC (Growth%)', width: '110px' },
            { label: 'Volumne PC (Growth%)' },
            { label: 'Transaction (Growth%)' },
            { label: 'Net Revenue (Growth%)' },
            { label: 'NR/UC (Growth%)' },
          ]
        : [
            { label: 'Bottler GP (Growth %)', width: '110px' },
            { label: 'OU GP (Growth %)' },
            { label: 'System GP (Growth %)' },
            { label: 'Bottler GP (%)' },
            { label: 'BU GP (%)' },
            { label: 'OU GP (%)' },
          ];
    return headers.map(({ label, width }) => (
      <TableCell
        key={label}
        sx={{
          ...headerCellStyle,
          borderRight:
            val === 'base' && (label === 'NR/UC (Growth%)' || label === 'OU GP (%)')
              ? '4px solid white'
              : '2px solid white',
          ...(width && { width }),
        }}
      >
        {label}
      </TableCell>
    ));
  };

  const rowCell = (val: any, val_percent: any, isBorder: boolean, type: any, isPercentageField: boolean = false) => {
    return (
      <>
        <TableCell
          style={{
            background: type === 'base' ? 'rgba(248, 248, 248, 0.59)' : 'white',
          }}
          sx={{
            ...bodyCellStyle,
            width: type === 'base' ? '80px' : '110px',
            borderRight: isBorder === true ? '4px solid white !important' : '2px solid white !important',
          }}
        >
          <Grid
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              whiteSpace: 'nowrap',
              paddingBottom: '12px',
              paddingTop: '12px',
              opacity: type === 'base' ? '1' : '0.7',
            }}
          >
            <Typography sx={typographyStyle}>
              {isPercentageField ? formatNumber(val * 100, 1) + '%' : formatNumber(val, 1)}
            </Typography>
            {val_percent === 0 && (
              <Typography
                sx={smallTypographyStyle}
                style={{
                  color: '#2EB28B',
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '2px',
                }}
              >
                <span style={{ marginRight: '1px' }}>▲</span>
                0.0%
              </Typography>
            )}
            {val_percent !== 0 && (
              <Typography
                sx={smallTypographyStyle}
                style={{
                  color: val_percent > 0 ? '#2EB28B' : 'red',
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '2px',
                }}
              >
                {val_percent > 0 ? <span>▲</span> : <span>▼</span>}
                {formatNumber(val_percent * 100, 1)}%
              </Typography>
            )}
          </Grid>
        </TableCell>
      </>
    );
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, type: 'base' | 'child') => {
    dispatch(setActiveMenu(type));
    dispatch(setAnchorEl(event.currentTarget));
  };

  const handleScenarioSelect = async (scenario: string, type: 'base' | 'child') => {
    await dispatch(
      setSelectedScenario({
        type,
        scenario,
      }),
    );
    await dispatch(setAnchorEl(null));
    await dispatch(showLoader());
    const payload = {
      scenario_1: type === 'base' ? scenario : selectedScenarios.base,
      scenario_2: type === 'child' ? scenario : selectedScenarios.child,
      brand: selectedBrands,
      bottler: enabledKeys.bottler,
      state: enabledKeys.state,
      select: SCENARIO_OUTPUT_DATA.SELECT_MAPPER[selectedToggle as SelectMapperKeys],
      view_by: SCENARIO_OUTPUT_DATA.VIEW_BY_MAPPER_DEEPDIVE[selectedViewBy as ViewByMapperKeysForDeepdive],
    };
    await dispatch(fetchSegmentDeepDive(payload));
    await dispatch(hideLoader());
  };

  const handleCloseMenu = () => {
    dispatch(setAnchorEl(null));
  };

  const renderBodyForRGM = (row: any, type: any) => {
    return (
      <>
        {rowCell(row[`scenario_1_volume_uc`], row[`scenario_1_volume_uc_growth`], false, type, false)}
        {rowCell(row[`scenario_1_volume_pc`], row[`scenario_1_volume_pc_growth`], false, type, false)}
        {rowCell(row[`scenario_1_transaction`], row[`scenario_1_transaction_growth`], false, type, false)}
        {rowCell(row[`scenario_1_net_revenue`], row[`scenario_1_net_revenue_growth`], false, type, false)}
        {rowCell(row[`scenario_1_nr_by_uc`], row[`scenario_1_nr_by_uc_growth`], true, type, false)}
        {rowCell(row[`scenario_2_volume_uc`], row[`scenario_2_volume_uc_growth`], false, type, false)}
        {rowCell(row[`scenario_2_volume_pc`], row[`scenario_2_volume_pc_growth`], false, type, false)}
        {rowCell(row[`scenario_2_transaction`], row[`scenario_2_transaction_growth`], false, type, false)}
        {rowCell(row[`scenario_2_net_revenue`], row[`scenario_2_net_revenue_growth`], false, type, false)}
        {rowCell(row[`scenario_2_nr_by_uc`], row[`scenario_2_nr_by_uc_growth`], false, type, false)}
      </>
    );
  };

  const renderBodyForFinance = (row: any, type: any) => {
    return (
      <>
        {rowCell(row[`scenario_1_bottler_gp`], row[`scenario_1_bottler_gp_growth`], false, type, false)}
        {rowCell(row[`scenario_1_ou_gp`], row[`scenario_1_ou_gp_growth`], false, type, false)}
        {rowCell(row[`scenario_1_system_gp`], row[`scenario_1_system_gp_growth`], true, type, false)}
        {rowCell(row[`scenario_1_bottler_gp_percent`], null, false, type, true)}
        {rowCell(row[`scenario_1_system_gp_percent`], null, false, type, true)}
        {rowCell(row[`scenario_1_ou_gp_percent`], null, true, type, true)}
        {rowCell(row[`scenario_2_bottler_gp`], row[`scenario_2_bottler_gp_growth`], false, type)}
        {rowCell(row[`scenario_2_ou_gp`], row[`scenario_2_ou_gp_growth`], false, type)}
        {rowCell(row[`scenario_2_system_gp`], row[`scenario_2_system_gp_growth`], true, type)}
        {rowCell(row[`scenario_2_bottler_gp_percent`], null, false, type, true)}
        {rowCell(row[`scenario_2_system_gp_percent`], null, false, type, true)}
        {rowCell(row[`scenario_2_ou_gp_percent`], null, false, type, true)}
      </>
    );
  };

  return (
    <ReviewTableContainer>
      <Table stickyHeader sx={{ tableLayout: 'fixed', minWidth: 800 }}>
        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
          <TableRow>
            <TableCell rowSpan={2} sx={{ ...headerCellStyle, width: '10%', borderBottom: '' }}>
              Segment
            </TableCell>
            <TableCell
              style={{ background: '#E81C0E', color: 'white' }}
              colSpan={selectedDeepdiveToggle === 'rgm' ? 5 : 6}
              sx={{
                ...headerCellStyle,
                width: '45%',
                borderRight: '4px solid white',
              }}
            >
              {selectedScenarios.base}
              <IconButton size="small" style={{ color: 'white' }} onClick={(e) => handleOpenMenu(e, 'base')}>
                {false ? (
                  <KeyboardArrowUp style={{ fontSize: '15px' }} />
                ) : (
                  <KeyboardArrowDown style={{ fontSize: '15px' }} />
                )}
              </IconButton>
            </TableCell>
            <TableCell
              style={{ background: '#E81C0E', color: 'white' }}
              colSpan={selectedDeepdiveToggle === 'rgm' ? 5 : 6}
              sx={{ ...headerCellStyle, width: '45%', paddingBottom: '2px' }}
            >
              {selectedScenarios.child}
              <IconButton size="small" style={{ color: 'white' }} onClick={(e) => handleOpenMenu(e, 'child')}>
                {false ? (
                  <KeyboardArrowUp style={{ fontSize: '15px' }} />
                ) : (
                  <KeyboardArrowDown style={{ fontSize: '15px' }} />
                )}
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                PaperProps={{
                  style: {
                    width: '186px',
                    fontSize: '9px',
                    height: '150px',
                    padding: '0px',
                  },
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                MenuListProps={{
                  style: {
                    padding: 0,
                  },
                }}
              >
                <MenuContainer
                  style={{
                    maxHeight: '150px',
                  }}
                >
                  <ScenarioMenuContainer style={{ padding: '0px' }}>
                    {scenarios.map((name: any) => (
                      <MenuItem
                        style={{
                          padding: '5px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          minHeight: 'auto',
                          paddingTop: '8px',
                          paddingBottom: '8px',
                        }}
                        key={name}
                        onClick={() => handleScenarioSelect(name, activeMenu as 'base' | 'child')}
                      >
                        <Grid style={{ display: 'flex' }}>
                          <StyledCheckbox
                            style={{ padding: '0px', marginTop: '5px' }}
                            value={name}
                            checked={
                              activeMenu === 'base' ? selectedScenarios.base === name : selectedScenarios.child === name
                            }
                            onChange={() => handleScenarioSelect(name, activeMenu as 'base' | 'child')}
                          />
                          <ListItemText
                            style={{
                              padding: '0px',
                              marginTop: '5px',
                            }}
                            primary={name}
                          />
                        </Grid>
                      </MenuItem>
                    ))}
                  </ScenarioMenuContainer>
                </MenuContainer>
              </Menu>
            </TableCell>
          </TableRow>
          <TableRow>
            {subHeaders('base')}

            {subHeaders('child')}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedFilteredData.map((row, index) => {
            const isExpanded = expandedRows.has(row.segment);
            const isLastRow = index === sortedFilteredData.length - 1;
            return (
              <React.Fragment key={row.segment}>
                <TableRow
                  sx={{
                    height: 30,
                    fontSize: 10,
                    whiteSpace: 'nowrap',
                    backgroundColor: isLastRow ? '#F2F2F2' : 'inherit',
                  }}
                >
                  <TableCell
                    style={{
                      whiteSpace: 'nowrap',
                      justifyContent: 'space-between',
                      background: row.segment === 'Total' ? '#AFA8A8' : '#F2F2F2',
                      color: row.segment === 'Total' ? 'white' : 'black',
                    }}
                    sx={bodyCellStyle}
                  >
                    <Grid
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignContent: 'center',
                        alignItems: 'center',
                        paddingLeft: '5px',
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: '10.89px',
                          lineHeight: '13.28px',
                          fontWeight: 400,
                          background: row.segment === 'Total' ? '#AFA8A8' : '#F2F2F2',
                        }}
                      >
                        {row.segment}
                      </Typography>
                      {row.segment !== 'Total' && (
                        <IconButton
                          size="small"
                          style={{ color: 'black' }}
                          onClick={() => handleExpandClick(row.segment)}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUp style={{ fontSize: '15px' }} />
                          ) : (
                            <KeyboardArrowDown style={{ fontSize: '15px' }} />
                          )}
                        </IconButton>
                      )}
                    </Grid>
                  </TableCell>
                  {selectedDeepdiveToggle === 'rgm' ? renderBodyForRGM(row, 'base') : renderBodyForFinance(row, 'base')}
                </TableRow>
                <TableRow>
                  <TableCell colSpan={15} sx={{ padding: 0 }}>
                    <Collapse in={isExpanded}>
                      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 800 }}>
                        <TableBody>
                          {row.pack.map((packRow: any) => (
                            <TableRow
                              key={packRow.pack_size}
                              sx={{
                                backgroundColor: '#ffffff',
                                fontSize: 12,
                                display: 'flex',
                              }}
                            >
                              <Grid style={{ width: '10%' }}>
                                <TableCell
                                  sx={{
                                    ...bodyCellStyle,
                                    fontSize: '10.89px',
                                    width: '123px',
                                    alignContent: 'center',
                                    alignItems: 'center',
                                    paddingTop: '10% ',
                                    paddingLeft: '50px',
                                    opacity: '0.7',
                                  }}
                                >
                                  {packRow.pack_size}
                                </TableCell>
                              </Grid>
                              <Grid style={{ width: '90%', background: 'white' }}>
                                {selectedDeepdiveToggle === 'rgm'
                                  ? renderBodyForRGM(packRow, 'child')
                                  : renderBodyForFinance(packRow, 'child')}
                              </Grid>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </ReviewTableContainer>
  );
};

export default NestedTable;
